import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CircularProgress,
  createStyles,
  lighten,
  LinearProgress,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@material-ui/core';
import { OpenInNew } from '@material-ui/icons';
import React, { useCallback } from 'react';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import { DownloadBag } from './useAnalysisDownload';

export interface OngoingDownloadsProps {
  downloadBag: DownloadBag;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      width: 500,
      marginTop: theme.spacing(1),
    },
    text: {
      height: 28,
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    buttonHover: {
      backgroundColor: lighten(theme.palette.primary.light, 0.7),
      '&:hover': {
        backgroundColor: lighten(theme.palette.primary.light, 0.7),
      },
    },
    tableRowButton: {
      cursor: 'pointer',
    },
    tableDisabled: {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.disabled,
    },
  })
);

export const OngoingDownloads: React.FC<OngoingDownloadsProps> = ({ downloadBag }) => {
  const classes = useClasses();
  const onOpenChange = useCallback(
    (open?: boolean) => downloadBag.setDownloadState((prev) => ({ ...prev, open: open })),
    []
  );

  return (
    <VoxelPopover open={downloadBag.downloadState.open} onOpenChange={onOpenChange}>
      <VoxelPopoverTrigger enableActive>
        <VoxelIconButton
          tooltip="Toogle downloads"
          icon={<FontAwesomeIcon icon={faDownload} size="lg" />}
          size="md"
        >
          {downloadBag.downloading && (
            <LinearProgress className="absolute w-[12px] ml-[1px] mt-[24px]" />
          )}
        </VoxelIconButton>
      </VoxelPopoverTrigger>
      <VoxelPopoverContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Export</TableCell>
              <TableCell width={'40%'}>Status</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {downloadBag.downloadState.downloads.map((download) => (
              <TableRow
                key={download.id}
                hover={download.state === 'completed'}
                className={
                  download.state === 'completed' ? classes.tableRowButton : classes.tableDisabled
                }
                onClick={(event) => {
                  if (download.state !== 'completed') return;
                  if (download.zip) {
                    window.location.href = download.downloadUrl;
                    downloadBag.setDownloadState((prev) => ({ ...prev, open: false }));
                    return;
                  } else {
                    window.open(download.downloadUrl, '_blank');
                    return;
                  }
                }}
              >
                <TableCell className={download.state !== 'completed' && classes.tableDisabled}>
                  {download.name}
                </TableCell>
                <TableCell className={download.state !== 'completed' && classes.tableDisabled}>
                  {download.state === 'downloading' &&
                    download.totalScreenshots !== download.completedScreenshots && (
                      <>
                        Captured {download.completedScreenshots} of {download.totalScreenshots}{' '}
                        charts
                      </>
                    )}
                  {download.state === 'downloading' &&
                    download.totalScreenshots === download.completedScreenshots &&
                    download.type === 'slide' &&
                    'Creating presentation'}
                  {download.state === 'downloading' &&
                    download.totalScreenshots === download.completedScreenshots &&
                    download.type === 'screenshot' &&
                    'Zipping images'}
                  {download.state === 'failed' && <>Failed</>}
                  {download.state === 'completed' && 'Ready'}
                </TableCell>
                <TableCell
                  padding="checkbox"
                  align="right"
                  className={download.state !== 'completed' && classes.tableDisabled}
                >
                  {download.state === 'downloading' && <CircularProgress size={18} />}
                  {download.state === 'completed' && <OpenInNew fontSize={'small'} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </VoxelPopoverContent>
    </VoxelPopover>
  );
};

export default OngoingDownloads;
